/**
 * Harbor module role-based access control.
 *
 * Harbor is the unified entry point for delivery-related workflows.
 * Tile visibility inside Harbor is driven by the user's role name.
 */

/**
 * Roles that have full write access in Perfect Truck
 * (certification button, editing, table interaction).
 */
export const PERFECT_TRUCK_FULL_ACCESS_ROLES: readonly string[] = [
  'Team Lead',
  'Inventory Coordinator',
  'Certifier',
  'Checker',
  'Site Supervisor',
  'Site Leader',
  'COE',
  'Developer',
  'Super User',
];

/**
 * Perfect Truck tile is visible to all Harbor users.
 * Full-access actions are still restricted by PERFECT_TRUCK_FULL_ACCESS_ROLES.
 */
export const PERFECT_TRUCK_HARBOR_ROLES: readonly string[] = ['*'];

/** Roles that can access the Delivery RPM tile within Harbor. */
export const DELIVERY_RPM_ROLES: readonly string[] = [
  'Delivery Driver',
  'Delivery District Leader',
  'Area Delivery Manager',
  'Delivery Market Director',
  'Delivery Market Leader',
  'Delivery Senior Leadership',
  'Super User',
  'Developer',
];

/**
 * Roles that have full write access in Delivery RPM
 * (SSCC entry, scanning, table interaction).
 */
export const DELIVERY_RPM_FULL_ACCESS_ROLES: readonly string[] = [
  'Delivery Driver',
  'Delivery District Leader',
  'Area Delivery Manager',
  'Delivery Market Director',
  'Delivery Market Leader',
  'Delivery Senior Leadership',
  'Delivery COE',
  'Super User',
  'Developer',
];

/**
 * Any role NOT in DELIVERY_RPM_FULL_ACCESS_ROLES gets read-only access in Delivery RPM
 * (table viewing and filtering only — no SSCC entry/scanning).
 */

/** Returns true when the user's role allows access to Perfect Truck via Harbor. */
export const canAccessPerfectTruckViaHarbor = (roleName?: string | null): boolean => {
  return Boolean(roleName);
};

/** Returns true when the user has full write access in Perfect Truck (certification button + editing). */
export const hasPerfectTruckFullAccess = (roleName?: string | null): boolean => {
  if (!roleName) return false;
  return PERFECT_TRUCK_FULL_ACCESS_ROLES.includes(roleName);
};

/** Returns true when the user has read-only access in Perfect Truck (roles NOT in full access list). */
export const hasPerfectTruckReadOnlyAccess = (roleName?: string | null): boolean => {
  if (!roleName) return false;
  return !PERFECT_TRUCK_FULL_ACCESS_ROLES.includes(roleName);
};

/** Returns true when the user's role allows access to Delivery RPM via Harbor (full access or read-only). */
export const canAccessDeliveryRPM = (roleName?: string | null): boolean => {
  if (!roleName) return false;
  return hasDeliveryRPMFullAccess(roleName) || hasDeliveryRPMReadOnlyAccess(roleName);
};

/** Returns true when the user has full write access in Delivery RPM (SSCC entry + scanning). */
export const hasDeliveryRPMFullAccess = (roleName?: string | null): boolean => {
  if (!roleName) return false;
  return DELIVERY_RPM_FULL_ACCESS_ROLES.includes(roleName);
};

/** Returns true when the user has read-only access in Delivery RPM (roles NOT in full access list). */
export const hasDeliveryRPMReadOnlyAccess = (roleName?: string | null): boolean => {
  if (!roleName) return false;
  return !DELIVERY_RPM_FULL_ACCESS_ROLES.includes(roleName);
};

/**
 * Returns true when the user qualifies for at least one Harbor tile.
 * Used to control Harbor sidebar section visibility.
 */
export const hasHarborAccess = (roleName?: string | null): boolean =>
  canAccessPerfectTruckViaHarbor(roleName) || canAccessDeliveryRPM(roleName);
